import { useState, useRef, useEffect } from "react";
import { FiTrash2, FiMinus, FiPlus } from "react-icons/fi";
import { FaEye, FaInfoCircle, FaPlus } from "react-icons/fa";
import OtherChargesDrawer from "../common/OtherChargesDrawer";
import ProductCatalog from "../common/ProductCatalog";
import SelectAddOnsDrawer from "../../pages/Orders/SelectAddOnsDrawer";

const CheckoutItemTable = ({
  serviceType = "grooming", // 'grooming' | 'boarding' | 'vet'
  service = {},
  pet = {},
  getPetIcon,
  serviceStatus = "",
  isOrderClosed = false,
  priceLoading = false,
  nights = 1,
  clientId,
  serviceCategoryId,
  petTypeId,
  serviceTypeName = "Grooming",
  drawerWidth = "w-full md:w-[90vw] lg:w-[90vw] xl:w-[85vw] 2xl:w-[80vw]",
  productsEndpoint = "/vendorproduct/getAvailableInventoryProducts",
  renderServiceSelector,
  renderMetaInfo,
  onUpdateService = () => {},
  onViewActivity,
  surcharges = [],
  extraRows = null,
}) => {
  const [addonsDrawerOpen, setAddonsDrawerOpen] = useState(false);
  const [productsDrawerOpen, setProductsDrawerOpen] = useState(false);
  const [otherChargesDrawerOpen, setOtherChargesDrawerOpen] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const breakdownRef = useRef(null);

  // Close breakdown popover on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (breakdownRef.current && !breakdownRef.current.contains(e.target)) {
        setShowBreakdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addons = service.addons || [];
  const products = service.products || [];
  const otherCharges = service.otherCharges || [];

  // ── Drawer Handlers ──
  const handleSaveAddons = (newAddonItems) => {
    const normalized = Array.isArray(newAddonItems)
      ? newAddonItems
      : [newAddonItems];
    const merged = [...addons];
    normalized.forEach((newItem) => {
      const idx = merged.findIndex(
        (item) => item.addon_id === newItem.addon_id,
      );
      if (idx !== -1) {
        merged[idx] = {
          ...merged[idx],
          quantity: (merged[idx].quantity || 0) + (newItem.quantity || 1),
        };
      } else {
        merged.push(newItem);
      }
    });
    onUpdateService({ ...service, addons: merged });
    setAddonsDrawerOpen(false);
  };

  const handleSaveProducts = (updatedProducts) => {
    setProductsDrawerOpen(false);

    const roundToTwo = (num) => {
      if (num === null || num === undefined || isNaN(num)) return 0;
      return Math.round((parseFloat(num) + Number.EPSILON) * 100) / 100;
    };

    const formattedProducts = (updatedProducts || []).map((product) => {
      const effectivePrice = Number(
        product.sale_price_after_discount ||
          product.effective_sale_price ||
          product.price ||
          0,
      );
      const mrpPrice = Number(
        product.mrp || product.mrp_price || product.sale_price || 0,
      );
      const gstSlab = Number(product.gst_slab || product.tax_percentage || 0);
      const quantity = Number(product.quantity || 1);

      let priceWithoutTax = effectivePrice;
      let gstAmount = 0;

      if (gstSlab > 0 && effectivePrice > 0) {
        priceWithoutTax = effectivePrice / (1 + gstSlab / 100);
        gstAmount = effectivePrice - priceWithoutTax;
      }

      return {
        ...product,
        product_id: product.id || product._id,
        product_name: product.name,
        quantity: quantity,
        unit_price: roundToTwo(effectivePrice),
        price: roundToTwo(priceWithoutTax),
        mrp_price: roundToTwo(mrpPrice),
        sale_price: roundToTwo(mrpPrice),
        total_price: roundToTwo(effectivePrice * quantity),
        gst: roundToTwo(gstAmount),
        gstAmount: roundToTwo(gstAmount),
        gstAmountAfterDiscount: roundToTwo(gstAmount),
        tax_percentage: gstSlab,
        tax_amount: roundToTwo(gstAmount),
        amount_without_tax: roundToTwo(priceWithoutTax),
        amount_with_tax: roundToTwo(effectivePrice),
        amount_before_discount: roundToTwo(mrpPrice),
        amount_after_discount: roundToTwo(effectivePrice),
        description: product.description || "",
        sku_number: product.sku_number || "",
        gst_slab: gstSlab,
      };
    });

    const merged = [...products];
    formattedProducts.forEach((newItem) => {
      const existingIndex = merged.findIndex(
        (item) => item.product_id === newItem.product_id,
      );
      if (existingIndex !== -1) {
        const existing = merged[existingIndex];
        const newQuantity = existing.quantity + newItem.quantity;
        merged[existingIndex] = {
          ...newItem,
          quantity: newQuantity,
          total_price: newItem.unit_price * newQuantity,
          amount_without_tax: newItem.amount_without_tax * newQuantity,
        };
      } else {
        merged.push(newItem);
      }
    });
    onUpdateService({ ...service, products: merged });
  };

  const handleSaveOtherCharges = (charges) => {
    onUpdateService({ ...service, otherCharges: charges });
    setOtherChargesDrawerOpen(false);
  };

  const removeAddon = (i) => {
    const updated = addons.filter((_, idx) => idx !== i);
    onUpdateService({ ...service, addons: updated });
  };

  const removeProduct = (i) => {
    const updated = products.filter((_, idx) => idx !== i);
    onUpdateService({ ...service, products: updated });
  };

  const adjustProductQty = (i, delta) => {
    const updated = [...products];
    const newQty = (updated[i].quantity || 1) + delta;
    if (newQty < 1) return removeProduct(i);
    updated[i] = { ...updated[i], quantity: newQty };
    onUpdateService({ ...service, products: updated });
  };

  const removeCharge = (i) => {
    const updated = otherCharges.filter((_, idx) => idx !== i);
    onUpdateService({ ...service, otherCharges: updated });
  };

  // ── Themes by Service Type ──
  const themeMap = {
    boarding: {
      headerBg: "bg-purple-50",
      headerBorder: "border-purple-200",
      badgeBg: "bg-purple-100",
      badgeText: "text-purple-800",
      badgeBorder: "border-purple-300",
      typeLabel: "Boarding",
    },
    grooming: {
      headerBg: "bg-pink-50",
      headerBorder: "border-pink-200",
      badgeBg: "bg-pink-100",
      badgeText: "text-pink-800",
      badgeBorder: "border-pink-300",
      typeLabel: "Grooming",
    },
    vet: {
      headerBg: "bg-emerald-50",
      headerBorder: "border-emerald-200",
      badgeBg: "bg-emerald-100",
      badgeText: "text-emerald-800",
      badgeBorder: "border-emerald-300",
      typeLabel: "Vet Clinic",
    },
    vet_clinic: {
      headerBg: "bg-emerald-50",
      headerBorder: "border-emerald-200",
      badgeBg: "bg-emerald-100",
      badgeText: "text-emerald-800",
      badgeBorder: "border-emerald-300",
      typeLabel: "Vet Clinic",
    },
  };
  const currentTheme = themeMap[serviceType] || themeMap.grooming;

  // ── Financial values ──
  const fmt = (v) => (Number(v) || 0).toFixed(2);

  const totalSurcharges =
    (surcharges || []).reduce((acc, s) => acc + (Number(s.price) || 0), 0) ||
    service.surchargesTotal ||
    0;

  const totalAmount = Number(service.price || 0);
  const discountAmount = Number(service.discount_amount || 0);
  const taxAmount = Number(service.gst_amount || 0);

  // MRP is tax-inclusive amount before discount (e.g. 1000 service with tax + 30 surcharge = 1030)
  const mrpAmount = Number(
    service.amount_before_discount ||
      service.mrp_price ||
      (totalAmount + discountAmount),
  );
  const basePrice = Math.max(0, mrpAmount - totalSurcharges);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-4">
      {/* ── Card Header: Status + Actions ── */}
      <div
        className={`flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 ${currentTheme.headerBg} border-b ${currentTheme.headerBorder}`}
      >
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span
            className={`text-xs font-bold uppercase px-2.5 py-0.5 rounded-full border ${currentTheme.badgeBg} ${currentTheme.badgeText} ${currentTheme.badgeBorder}`}
          >
            {currentTheme.typeLabel}
          </span>
          {serviceStatus && (
            <span
              className={`text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize border ${
                ["completed", "complete", "closed"].includes(
                  serviceStatus.toLowerCase(),
                )
                  ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                  : ["started", "checkin", "check_in", "in_progress"].includes(
                        serviceStatus.toLowerCase(),
                      )
                    ? "bg-blue-100 text-blue-800 border-blue-200"
                    : ["cancelled", "canceled", "no show"].includes(
                          serviceStatus.toLowerCase(),
                        )
                      ? "bg-rose-100 text-rose-800 border-rose-200"
                      : ["checkout", "checked_out"].includes(
                            serviceStatus.toLowerCase(),
                          )
                        ? "bg-purple-100 text-purple-800 border-purple-200"
                        : "bg-gray-100 text-gray-800 border-gray-200"
              }`}
            >
              {serviceStatus}
            </span>
          )}
        </div>

        {/* Right side: Action buttons & View Activity */}
        <div className="flex items-center gap-2 shrink-0">
          {!isOrderClosed && (
            <>
              <button
                type="button"
                onClick={() => setAddonsDrawerOpen(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-teal-600 text-white text-xs font-medium hover:bg-teal-700 transition shadow-sm"
              >
                <FaPlus className="w-2.5 h-2.5" /> Add‑on
              </button>
              <button
                type="button"
                onClick={() => setProductsDrawerOpen(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition shadow-sm"
              >
                <FaPlus className="w-2.5 h-2.5" /> Product
              </button>
              <button
                type="button"
                onClick={() => setOtherChargesDrawerOpen(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-purple-600 text-white text-xs font-medium hover:bg-purple-700 transition shadow-sm"
              >
                <FaPlus className="w-2.5 h-2.5" /> Other Charge
              </button>
              <span className="w-px h-5 bg-gray-300 mx-1" />
            </>
          )}

          {onViewActivity && (
            <button
              type="button"
              onClick={() => onViewActivity(service)}
              className="p-1.5 rounded-full bg-white hover:bg-blue-50 text-blue-500 hover:text-blue-700 border border-gray-200 transition shadow-sm"
              title="View Activity"
            >
              <FaEye size={14} />
            </button>
          )}
        </div>
      </div>

      {/* ── Table Wise Item Display ── */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border-collapse">
          <thead className="bg-gray-50 text-gray-600 uppercase text-xs tracking-wider border-b border-gray-200">
            <tr>
              <th className="px-3 py-2.5 text-left min-w-[280px]">Item Details</th>
              <th className="px-2 py-2.5 text-center w-14">Qty</th>
              <th className="px-3 py-2.5 text-right whitespace-nowrap">MRP (INCL. TAX)</th>
              <th className="px-3 py-2.5 text-right">Discount</th>
              <th className="px-3 py-2.5 text-right">Tax</th>
              <th className="px-3 py-2.5 text-right">Total</th>
              <th className="px-3 py-2.5 text-center w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {/* 1. Main Service Row */}
            <tr className="hover:bg-gray-50/60 transition-colors">
              <td className="px-3 py-3 align-top">
                <div className="flex flex-col gap-1.5">
                  {/* Row 1: Pet info (Prominent, larger pet name) */}
                  <div className="flex items-center gap-2 flex-wrap leading-tight">
                    {getPetIcon ? getPetIcon(pet?.petType) : null}
                    <span className="text-base font-bold text-gray-900 tracking-tight">
                      {pet?.name || "Unknown Pet"}
                    </span>
                    <span className="text-xs text-gray-500 font-normal">
                      ({pet?.breed || ""}, {pet?.size || ""}
                      {pet?.weight ? `, ${pet.weight}` : ""})
                    </span>
                  </div>

                  {/* Row 2: Service Selector + Date beside it */}
                  <div className="flex items-center gap-2 flex-wrap mt-0.5">
                    {renderServiceSelector && renderServiceSelector()}
                    {renderMetaInfo && renderMetaInfo()}
                  </div>
                </div>
              </td>

              <td className="px-2 py-2.5 text-center align-top text-sm font-medium text-gray-700">
                -
              </td>

              <td className="px-3 py-2.5 text-right whitespace-nowrap align-top font-medium text-gray-700">
                ₹{fmt(mrpAmount)}
              </td>

              <td className="px-3 py-2.5 text-right whitespace-nowrap text-red-600 align-top">
                {discountAmount > 0 ? `− ₹${fmt(discountAmount)}` : "—"}
              </td>

              <td className="px-3 py-2.5 text-right whitespace-nowrap text-gray-600 align-top">
                {taxAmount > 0 ? `₹${fmt(taxAmount)}` : "—"}
              </td>

              <td className="px-3 py-2.5 text-right whitespace-nowrap text-green-700 font-bold align-top">
                {priceLoading ? (
                  <span className="text-xs text-gray-400 italic">Calculating...</span>
                ) : (
                  `₹${fmt(totalAmount)}`
                )}
              </td>

              {/* Price Breakdown Popover Icon */}
              <td className="px-3 py-2.5 text-center align-top relative" ref={breakdownRef}>
                {totalAmount > 0 && !priceLoading && (
                  <button
                    type="button"
                    onClick={() => setShowBreakdown((prev) => !prev)}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition inline-flex items-center justify-center"
                    title="View Price Breakdown"
                  >
                    <FaInfoCircle size={14} />
                  </button>
                )}

                {showBreakdown && totalAmount > 0 && (
                  <div className="absolute top-full right-2 mt-1 w-64 bg-white rounded-xl shadow-xl border border-gray-200 z-50 p-3 text-xs text-left">
                    <div className="font-semibold text-gray-800 mb-2 border-b pb-1.5 flex justify-between">
                      <span>Price Breakdown</span>
                      <span className="text-gray-400 font-normal">₹{fmt(totalAmount)}</span>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Base Price</span>
                        <span>₹{fmt(basePrice)}</span>
                      </div>

                      {(surcharges || []).length > 0 && (
                        <div className="pl-2 border-l-2 border-gray-200 ml-0.5 space-y-0.5">
                          {surcharges.map((s, i) => (
                            <div key={i} className="flex justify-between text-gray-500">
                              <span className="capitalize">{s.name?.replace(/_/g, " ") || "Surcharge"}</span>
                              <span className="text-green-600">+₹{fmt(s.price)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between font-medium pt-0.5 border-t border-gray-100">
                            <span>Surcharges</span>
                            <span className="text-green-600">+₹{fmt(totalSurcharges)}</span>
                          </div>
                        </div>
                      )}

                      {discountAmount > 0 && (
                        <div className="flex justify-between text-red-500">
                          <span>Discount</span>
                          <span>−₹{fmt(discountAmount)}</span>
                        </div>
                      )}

                      {taxAmount > 0 && (
                        <div className="flex justify-between text-gray-600">
                          <span>Tax ({service.tax_percentage || 0}%)</span>
                          <span>₹{fmt(taxAmount)}</span>
                        </div>
                      )}

                      <div className="flex justify-between font-bold text-green-700 border-t pt-1.5 mt-1">
                        <span>Total</span>
                        <span>₹{fmt(totalAmount)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </td>
            </tr>

            {/* 2. Add-on Rows */}
            {addons.map((addon, idx) => {
              const amt = Number(addon.addon_price || addon.price || 0);
              const addonQty = Number(addon.quantity || 1);
              const totalAddon = amt * addonQty;
              return (
                <tr key={`addon-${idx}`} className="bg-green-50/25 hover:bg-green-50/50 transition-colors">
                  <td className="px-3 py-2 align-middle pl-6">
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-800">
                      <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-semibold uppercase">
                        Add‑on
                      </span>
                      <span className="font-medium">
                        {addon.addon_name || addon.name || `Add-on ${idx + 1}`}
                      </span>
                    </span>
                  </td>
                  <td className="px-2 py-2 text-center align-middle text-xs text-gray-700 font-medium">
                    -
                  </td>
                  <td className="px-3 py-2 text-right align-middle text-xs text-gray-700">
                    ₹{fmt(amt)}
                  </td>
                  <td className="px-3 py-2 text-right align-middle text-xs text-gray-400">—</td>
                  <td className="px-3 py-2 text-right align-middle text-xs text-gray-400">—</td>
                  <td className="px-3 py-2 text-right align-middle text-xs font-semibold text-green-700">
                    ₹{fmt(totalAddon)}
                  </td>
                  <td className="px-3 py-2 text-center align-middle">
                    {!isOrderClosed && (
                      <button
                        type="button"
                        onClick={() => removeAddon(idx)}
                        className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition"
                        title="Remove add-on"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}

            {/* 3. Product Rows */}
            {products.map((product, idx) => {
              const mrpPrice = Number(product.mrp_price || product.sale_price || 0);
              const effectivePrice = Number(product.unit_price || 0);
              const gstAmount = Number(product.gst || product.gstAmount || 0);
              const quantity = Number(product.quantity || 1);
              const totalEffective = effectivePrice * quantity;
              const totalGst = gstAmount * quantity;
              const totalMrp = (mrpPrice || effectivePrice) * quantity;
              const prodDiscount = Math.max(0, totalMrp - totalEffective);

              return (
                <tr key={`prod-${idx}`} className="bg-blue-50/25 hover:bg-blue-50/50 transition-colors">
                  <td className="px-3 py-2 align-middle pl-6">
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-800 flex-wrap">
                      <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold uppercase">
                        Product
                      </span>
                      <span className="font-medium">{product.product_name || product.name}</span>
                      {product.gst_slab > 0 && (
                        <span className="text-[11px] text-gray-500 italic">
                          ({product.gst_slab}% GST)
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-center align-middle">
                    <div className="inline-flex items-center justify-center gap-1">
                      {!isOrderClosed && (
                        <button
                          type="button"
                          onClick={() => adjustProductQty(idx, -1)}
                          className="w-4 h-4 rounded flex items-center justify-center hover:bg-gray-200 text-gray-600 transition"
                          title="Decrease"
                        >
                          <FiMinus className="w-2.5 h-2.5" />
                        </button>
                      )}
                      <span className="text-xs font-medium w-5 text-center">{quantity}</span>
                      {!isOrderClosed && (
                        <button
                          type="button"
                          onClick={() => adjustProductQty(idx, 1)}
                          className="w-4 h-4 rounded flex items-center justify-center hover:bg-gray-200 text-gray-600 transition"
                          title="Increase"
                        >
                          <FiPlus className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right align-middle text-xs text-gray-700">
                    ₹{fmt(totalMrp)}
                  </td>
                  <td className="px-3 py-2 text-right align-middle text-xs text-red-600">
                    {prodDiscount > 0 ? `− ₹${fmt(prodDiscount)}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right align-middle text-xs text-gray-600">
                    {totalGst > 0 ? `₹${fmt(totalGst)}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right align-middle text-xs font-semibold text-green-700">
                    ₹{fmt(totalEffective)}
                  </td>
                  <td className="px-3 py-2 text-center align-middle">
                    {!isOrderClosed && (
                      <button
                        type="button"
                        onClick={() => removeProduct(idx)}
                        className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition"
                        title="Remove product"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}

            {/* 4. Other Charges Rows */}
            {otherCharges.map((charge, idx) => {
              const amt = Number(charge.amount || 0);
              return (
                <tr key={`charge-${idx}`} className="bg-purple-50/25 hover:bg-purple-50/50 transition-colors">
                  <td className="px-3 py-2 align-middle pl-6">
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-800">
                      <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px] font-semibold uppercase">
                        Other
                      </span>
                      <span className="font-medium">
                        {charge.description || charge.name || "Charge"}
                      </span>
                    </span>
                  </td>
                  <td className="px-2 py-2 text-center align-middle text-xs text-gray-700 font-medium">
                    -
                  </td>
                  <td className="px-3 py-2 text-right align-middle text-xs text-gray-700">
                    ₹{fmt(amt)}
                  </td>
                  <td className="px-3 py-2 text-right align-middle text-xs text-gray-400">—</td>
                  <td className="px-3 py-2 text-right align-middle text-xs text-gray-400">—</td>
                  <td className="px-3 py-2 text-right align-middle text-xs font-semibold text-purple-700">
                    ₹{fmt(amt)}
                  </td>
                  <td className="px-3 py-2 text-center align-middle">
                    {!isOrderClosed && (
                      <button
                        type="button"
                        onClick={() => removeCharge(idx)}
                        className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition"
                        title="Remove charge"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}

            {/* 5. Extra Custom Rows (e.g. Consultation charges) */}
            {extraRows}
          </tbody>
        </table>
      </div>

      {/* ── Drawers for Add-ons, Products, Other Charges ── */}
      {addonsDrawerOpen && (
        <SelectAddOnsDrawer
          isOpen={addonsDrawerOpen}
          onClose={() => setAddonsDrawerOpen(false)}
          onSave={handleSaveAddons}
          serviceCategoryId={serviceCategoryId}
          petTypeId={petTypeId}
          selectedAddons={addons}
          serviceTypeName={serviceTypeName}
          isFullWidth={drawerWidth === "w-full"}
          drawerWidth={drawerWidth}
        />
      )}

      {productsDrawerOpen && (
        <ProductCatalog
          isOpen={productsDrawerOpen}
          onClose={() => setProductsDrawerOpen(false)}
          onCartUpdate={handleSaveProducts}
          apiEndpoint={productsEndpoint}
          isFullWidth={drawerWidth === "w-full"}
          drawerWidth={drawerWidth}
        />
      )}

      {otherChargesDrawerOpen && (
        <OtherChargesDrawer
          isOpen={otherChargesDrawerOpen}
          onClose={() => setOtherChargesDrawerOpen(false)}
          onSave={handleSaveOtherCharges}
          initialCharges={otherCharges}
          isFullWidth={drawerWidth === "w-full"}
          drawerWidth={drawerWidth}
        />
      )}
    </div>
  );
};

export default CheckoutItemTable;
